import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as TooltipUI } from '@/components/ui/tooltip';
import { useDashboard } from '@/contexts/DashboardContext';
import { formatSeconds, formatTrendsXAxisLabel } from '@/lib/utils';
import { LINE_CHART_COLORS } from '@/constants/colors';
import { WidgetData } from '@/types';
import { Info } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { GRID_CONFIG } from '@/lib/chartConfig';

interface WaitingTimeDataPoint {
  time: string;
  avgWaitTime: number;
}

interface TooltipPayloadItem {
  value: number;
  name: string;
  color: string;
  dataKey: keyof WaitingTimeDataPoint;
  payload: WaitingTimeDataPoint;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  theme?: string;
  rangeType?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

interface AverageWaitingTimeTrendWidgetProps {
  data: WidgetData;
  isGeneralMode?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  error?: string;
  rangeType?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

const CustomTooltip = ({ active, payload, label, theme, rangeType = 'weekly' }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    const isDark = theme === 'dark';
    return (
      <div className={`p-3 rounded-lg shadow-lg border ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'}`}>
        <p className="font-semibold mb-2">{formatTrendsXAxisLabel(label, rangeType)}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 mb-1">
            <span className="text-sm" style={{ color: isDark ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light }}>
              Average Wait Time: {formatSeconds(entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

interface ChartContentProps {
  data: WaitingTimeDataPoint[];
  theme: string;
  rangeType: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
}

const ChartContent = ({ data, theme, rangeType }: ChartContentProps) => {
  const isDark = theme === 'dark';

  // Check if all data points are 0
  const isAllDataZero = data?.every(item =>
    item.avgWaitTime === 0 || item.avgWaitTime == null
  ) ?? false;

  return (
    <div className="h-64">
      <ResponsiveContainer width="99%" height={280}>
        <LineChart data={data} margin={{ left: 20, right: 30, top: 20, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            stroke={isDark ? '#9ca3af' : '#6b7280'}
            tickFormatter={(value) => formatTrendsXAxisLabel(value, rangeType)}
            tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
          />
          <YAxis
            stroke={isDark ? '#9ca3af' : '#6b7280'}
            tickFormatter={formatSeconds}
            domain={isAllDataZero ? [0, 180] : [0, 'dataMax']}
            ticks={isAllDataZero ? [0, 30, 60, 90, 120, 150, 180] : undefined}
            tick={{ fontSize: 14, fill: theme === "dark" ? '#fff' : '#6b7280' }}
          />
          <Tooltip
            content={<CustomTooltip theme={theme} rangeType={rangeType} />}
            cursor={{ stroke: isDark ? '#4b5563' : '#d1d5db' }}
          />
          {!isAllDataZero && (
            <Line
              type="monotone"
              dataKey="avgWaitTime"
              stroke={isDark ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light}
              strokeWidth={2}
              dot={{ r: 5, stroke: isDark ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light, strokeWidth: 2, fill: isDark ? LINE_CHART_COLORS.DOT_FILL.dark : LINE_CHART_COLORS.DOT_FILL.light }}
              activeDot={{ r: 7, fill: isDark ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light, stroke: isDark ? LINE_CHART_COLORS.LINE.dark : LINE_CHART_COLORS.LINE.light, strokeWidth: 2 }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const AverageWaitingTimeTrendWidget: React.FC<AverageWaitingTimeTrendWidgetProps> = ({
  data,
  loading,
  error,
  rangeType = 'weekly'
}) => {
  const { theme } = useDashboard();
  const isDark = theme === 'dark';

  const chartData = useMemo(() => {
    if (!data?.value || !Array.isArray(data.value)) return [];

    return data.value as WaitingTimeDataPoint[];
  }, [data?.value]);

  const renderContent = () => {
    if (loading) {
      return <Skeleton className="w-full h-[300px] rounded-none" />;
    }

    if (error) {
      return <div className="text-red-500 text-center py-10">{error}</div>;
    }

    if (!Array.isArray(chartData) || chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground dark:text-white/60">
          <div className="flex flex-col items-center gap-2">
            <span>No call records were found</span>
          </div>
        </div>
      );
    }

    return <ChartContent data={chartData} theme={theme} rangeType={rangeType} />;
  };

  return (
    <Card className={`h-full ${isDark ? 'bg-gray-800 border-0 text-white' : 'bg-white'}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <span>Average Wait Time Trend</span>
          <TooltipProvider>
            <TooltipUI delayDuration={0}>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="max-w-xs text-xs font-normal">
                  The pattern of average amount of time that the callers had to wait for an agent to answer within a given time.
                </p>
              </TooltipContent>
            </TooltipUI>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="p-0">
          {renderContent()}
      </CardContent>
    </Card>
  );
};

export default AverageWaitingTimeTrendWidget;
